import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IVideoTutorial } from "../VideoPage/IVideoTutorial";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addVideoTutorial } from "../../Redux/Slices/VideoTutorials/AddVideoTutorialSlice";
import { editVideoTutorial } from "../../Redux/Slices/VideoTutorials/EditVideoTutorialSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";

interface VideoTutorialFormProps {
    formRef: React.RefObject<HTMLFormElement>;
    initialValues: Partial<IVideoTutorial>;
    setInitialValues: React.Dispatch<React.SetStateAction<Partial<IVideoTutorial>>>;
}

const VideoTutorialForm: FunctionComponent<VideoTutorialFormProps> = (props) => {
    const dispatch = useAppDispatch();

    const videoSchema = z.object({
        vid_tut_id: z.string().optional(),
        category_id: z.union([z.string(), z.number()], {
            required_error: "Please select category",
            invalid_type_error: "Please select category",
        }).refine(val => val !== undefined && val !== null && val !== "", {
            message: "Please select category",
        }),
        domain: z.string().optional(),
        yeargroup_region_id: z.number().nullable().optional(),
        free_tier: z.boolean(),
        show_in_dashboard_only: z.boolean(),
        video_title: z.string({
            required_error: "Please enter video title",
            invalid_type_error: "Please enter video title",
        }).min(1, "Please enter video title"),
        video_duration: z.string({
            required_error: "Please enter video duration",
            invalid_type_error: "Please enter video duration",
        }).min(1, "Please enter video duration"),
        video_url: z.string({
            required_error: "Please enter video URL",
            invalid_type_error: "Please enter video URL",
        }).url("Please enter a valid URL"),
        description: z.string().nullable().optional(),
        status: z.boolean(),
    });

    const methods = useForm<IVideoTutorial>({
        defaultValues: {
            ...props.initialValues as IVideoTutorial,
            free_tier: props.initialValues.free_tier ?? false,
            show_in_dashboard_only: props.initialValues.show_in_dashboard_only ?? false,
            status: props.initialValues.status ?? true,
        },
        resolver: zodResolver(videoSchema),
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<IVideoTutorial> = (val) => {
        if (val.vid_tut_id) {
            dispatch(editVideoTutorial(val));
        } else {
            dispatch(addVideoTutorial({ ...val, vid_tut_id: "0" }));
        }
    };

    // Fetch data from Redux
    const { data: domainsData } = useAppSelector((state) => state.ListPackageDomainSlice);
    const { data: categoriesData } = useAppSelector((state) => state.ListVideoTutorialsSlice);
    const { data: yearGroupMappingsData } = useAppSelector((state) => state.ListYearlevel_mapSlice);

    const statusOptions = [
        { label: "Active", value: true },
        { label: "Inactive", value: false },
    ];

    const booleanOptions = [
        { label: "Yes", value: true },
        { label: "No", value: false },
    ];

    // Follow the exact pattern from other forms: data.data
    const domainOptions: string[] = Array.isArray(domainsData?.data) ? domainsData!.data : [];
    const categoryOptions: AutocompleteOption[] = Array.isArray((categoriesData as any)?.data?.video_tutorial_categories) ? ((categoriesData as any).data.video_tutorial_categories as any[]).map((c: any) => ({
        label: c.meta_value,
        value: c.id
    })) : [];
    const yearGroupOptions: AutocompleteOption[] = Array.isArray(yearGroupMappingsData?.data) ? (yearGroupMappingsData!.data as any[]).map((y: any) => ({
        label: `${y.yeargroup?.name || ""}-${y.region?.name || ""}`,
        value: y.id
    })) : [];

    useEffect(() => {
        return () => {
            props.setInitialValues({});
        };
    }, []);

    return (
        <FormProvider {...methods}>
            <form
                ref={props.formRef}
                onSubmit={methods.handleSubmit(onSubmit)}
                className="form_content"
            >
                <TextInputFieldC name="video_title" label="Video Title" type="text" />
                <TextInputFieldC name="description" label="Description" type="text" multiline rows={3} />
                <TextInputFieldC name="video_url" label="Video URL" type="text" />
                <TextInputFieldC name="video_duration" label="Video Duration" type="text" />

                <Select
                    name="domain"
                    label="Domain"
                    options={domainOptions ?? []}
                    value={domainOptions?.find(opt => opt === methods.getValues("domain")) ?? null}
                    onChange={(_, value) => {
                        methods.setValue("domain", value as string, { shouldValidate: true });
                    }}
                />

                <Select
                    name="category_id"
                    label="Category"
                    options={categoryOptions ?? []}
                    value={categoryOptions?.find(opt => opt.value === methods.getValues("category_id")) ?? null}
                    onChange={(_, value) => {
                        const val = value as AutocompleteOption;
                        methods.setValue("category_id", val?.value as string | number, { shouldValidate: true });
                    }}
                />

                <Select
                    name="yeargroup_region_id"
                    label="Year Group Region"
                    options={yearGroupOptions ?? []}
                    value={yearGroupOptions?.find(opt => opt.value === methods.getValues("yeargroup_region_id")) ?? null}
                    onChange={(_, value) => {
                        const val = value as AutocompleteOption | null;
                        methods.setValue("yeargroup_region_id", val?.value as number ?? null, { shouldValidate: true });
                    }}
                />

                <Select
                    name="free_tier"
                    label="Free Tier"
                    options={booleanOptions}
                    value={booleanOptions.find(opt => opt.value === methods.getValues("free_tier")) ?? null}
                    onChange={(_, value) => {
                        const val = value as AutocompleteOption;
                        methods.setValue("free_tier", val?.value as boolean, { shouldValidate: true });
                    }}
                />

                <Select
                    name="show_in_dashboard_only"
                    label="Show in Dashboard Only"
                    options={booleanOptions}
                    value={booleanOptions.find(opt => opt.value === methods.getValues("show_in_dashboard_only")) ?? null}
                    onChange={(_, value) => {
                        const val = value as AutocompleteOption;
                        methods.setValue("show_in_dashboard_only", val?.value as boolean, { shouldValidate: true });
                    }}
                />

                <Select
                    name="status"
                    label="Status"
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === methods.getValues("status")) ?? null}
                    onChange={(_, value) => {
                        const val = value as AutocompleteOption;
                        methods.setValue("status", val?.value as boolean, { shouldValidate: true });
                    }}
                />

            </form>
        </FormProvider>
    );
};

export default memo(VideoTutorialForm);
